import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Dimensions, Platform, View, ViewStyle, TextStyle } from 'react-native'

import { color } from '../../theme'
import { SvgIcon } from '../svg-icon'
import { Text } from '../text'
import { iconStarWhite } from '../../assets'
import { Skeleton } from '../skeleton'

import { UserLevelAndPointsType } from '@trivie/core'
import { Level } from './level'
import LinearGradient from 'react-native-linear-gradient'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
export interface LevelProgressProps {
  base?: string
  cardStyle?: ViewStyle
  buttonStyle?: ViewStyle
  buttonTextStyle?: TextStyle
  titleElement?: JSX.Element
  levelAndPoints: UserLevelAndPointsType | null
  progressBackground?: Array<string>
  progressStyle?: ViewStyle
  didLevelUp?: boolean
  loading?: boolean
  pointsGained?: number
  total_points?: number
}

export const LevelProgress = observer((props: LevelProgressProps) => {
  const {
    cardStyle = {},
    titleElement,
    loading,
    levelAndPoints,
    progressBackground,
    progressStyle,
    didLevelUp = false,
    buttonStyle,
    buttonTextStyle,
    base,
    pointsGained = 0,
    total_points = 0,
  } = props
  const [totalPoints, setTotalPoints] = useState(0)
  const [widthOfBar, setwidthOfBar] = useState(0)
  const { t } = useTranslation()

  useEffect(() => {
    if (levelAndPoints) {
      if (pointsGained && total_points) {
        setTotalPoints(total_points - pointsGained)
        setwidthOfBar(
          didLevelUp ? 1 : (total_points - pointsGained) / (levelAndPoints?.level_max_points || 1),
        )
        setTimeout(() => {
          setwidthOfBar(didLevelUp ? 1 : total_points / (levelAndPoints?.level_max_points || 1))
          setTotalPoints(total_points)
        }, 1000)
      } else {
        setTotalPoints(total_points)
      }
    }
  }, [levelAndPoints])

  const renderTitle = () => {
    if (titleElement) return titleElement

    return (
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
        }}
      >
        <Level
          text={levelAndPoints?.user_level}
          level_icon={levelAndPoints?.level_icon}
          textStyle={{ color: color.white }}
          style={{
            backgroundColor: 'black',
            width: 32,
            height: 32,
          }}
        />
        <View style={{ paddingLeft: 10, top: levelAndPoints?.level_max_points !== null ? 4 : 0 }}>
          <Text style={{ fontWeight: '600', fontSize: 16 }}>{levelAndPoints?.label}</Text>
          {levelAndPoints?.level_max_points !== null && (
            <Text
              tx={`${(levelAndPoints?.level_max_points ?? 0) - total_points} ${t(
                'Profile.pointsToNextLevel',
              )}`}
              variant="body2Emphasized"
              style={{ color: color.shade50 }}
            />
          )}
        </View>
      </View>
    )
  }

  const progressBG = progressBackground || ['#F3B14E20', '#FFCE5120']
  const progressColorsT = ['#FFCE51', '#F3B14E']

  const getTextColorOfProgressScore = useMemo(() => {
    if (base === 'Profile') return widthOfBar > 55 ? color.darkenGrey : color.shade50
    return widthOfBar > 55 ? color.shade50 : color.white
  }, [widthOfBar])

  const renderPoints = useCallback(() => {
    if (didLevelUp) {
      return (
        <Text
          text={levelAndPoints?.level_min_points + ''}
          variant="body2Emphasized"
          style={{
            color: getTextColorOfProgressScore,
            alignSelf: 'center',
            textAlign: 'center',
          }}
        />
      )
    } else {
      return (
        <Text
          text={totalPoints.toString()}
          variant="body2Emphasized"
          style={{
            color: getTextColorOfProgressScore,
            alignSelf: 'center',
            textAlign: 'center',
          }}
        />
      )
    }
  }, [totalPoints])

  const renderRecallProgress = React.useMemo(() => {
    const _barValue = didLevelUp ? 1 : total_points / (levelAndPoints?.level_max_points || 1)
    const widthPercentage = (_barValue > 0.09 ? _barValue : 0.09) * 100
    return (
      <View
        style={{
          width: '100%',
          backgroundColor: color.darkProgressBg,
          padding: 6,
          borderRadius: 100,
          ...progressStyle,
        }}
      >
        <View style={{ position: 'absolute', left: 6, top: 6, zIndex: 999 }}>
          <Level
            level_icon={didLevelUp ? '' : levelAndPoints?.level_icon}
            text={didLevelUp ? (levelAndPoints?.user_level || 1) - 1 : levelAndPoints?.user_level}
            textStyle={{ color: color.white, ...buttonTextStyle }}
            style={{
              borderColor: progressBG[0],
              backgroundColor:
                levelAndPoints?.level_icon && !didLevelUp ? progressBG[0] : '#F3B14E',
              ...buttonStyle,
            }}
          />
        </View>
        <View style={{ width: `${widthPercentage}%` }}>
          <LinearGradient
            style={{
              width: '100%',
              borderRadius: 100,
              height: 26,
            }}
            colors={progressColorsT}
            locations={[0, 1]}
            angle={180}
            pointerEvents="none"
          />
        </View>
        <View style={{ position: 'absolute', right: 6, top: 6, zIndex: 999 }}>
          <Level
            level_icon={didLevelUp ? '' : levelAndPoints?.next_level_icon}
            text={
              didLevelUp ? levelAndPoints?.user_level || 1 : (levelAndPoints?.user_level || 1) + 1
            }
            textStyle={{ color: color.white, ...buttonTextStyle }}
            style={{
              opacity: base === 'Profile' ? 0.5 : 1,
              borderColor: progressBG[0],
              backgroundColor:
                levelAndPoints?.level_icon && !didLevelUp ? progressBG[0] : '#F3B14E',
              ...buttonStyle,
            }}
          />
        </View>
      </View>
    )
  }, [levelAndPoints, total_points])

  return loading ? (
    <Skeleton style={LOADER} />
  ) : levelAndPoints ? (
    <View style={{ ...VIEW_CARD, ...cardStyle }}>
      {renderTitle()}
      <View
        style={{
          paddingTop: 10,
          flexDirection: 'row',
          flexWrap: 'wrap',
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            width: '100%',
          }}
        >
          <View style={{ flex: 1, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            {renderRecallProgress}

            <View
              style={{
                flexDirection: 'row',
                height: '100%',
                width: '100%',
                position: 'absolute',
                alignItems: 'center',
                justifyContent: 'center',
                paddingLeft: 10,
              }}
            >
              <View style={{ marginRight: 5 }}>
                <SvgIcon
                  size={15}
                  src={iconStarWhite}
                  color={widthOfBar < 50 ? color.yellow : color.white}
                />
              </View>
              {renderPoints()}

              <Text
                text={
                  levelAndPoints?.level_max_points === null
                    ? ''
                    : `/${
                        didLevelUp
                          ? levelAndPoints?.level_min_points
                          : levelAndPoints?.level_max_points
                      }`
                }
                variant="body2Emphasized"
                style={{
                  color: getTextColorOfProgressScore,
                  alignSelf: 'center',
                  textAlign: 'center',
                }}
              />
            </View>
          </View>

          {didLevelUp && (
            <View style={{ position: 'absolute', right: 0, bottom: -5 }}>
              <LinearGradient
                colors={['#FDFDFD', '#FFDD64']}
                start={{ x: 0.0, y: 1.0 }}
                end={{ x: 1.0, y: 1.0 }}
                useAngle
                angle={180}
                style={{
                  height: 42,
                  width: 42,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 40,
                }}
              >
                <Level
                  levelUp={true}
                  level_icon={levelAndPoints?.level_icon}
                  iconW={40}
                  iconH={40}
                  text={levelAndPoints?.user_level}
                  textStyle={{
                    color: '#FDFDDD',
                    fontWeight: '800',
                    fontSize: 18,
                    ...buttonTextStyle,
                  }}
                  style={{
                    backgroundColor: color.black,
                    width: 34,
                    height: 34,
                  }}
                />
              </LinearGradient>
            </View>
          )}
        </View>
      </View>
    </View>
  ) : null
})

const VIEW_CARD: ViewStyle = {
  paddingHorizontal: 18,
  paddingVertical: 18,
  backgroundColor: '#fff',
  paddingTop: 18,
  borderRadius: 20,
  width: '100%',
}
const LOADER: ViewStyle = {
  height: 109,
  width: Platform.OS === 'web' ? '100%' : Dimensions.get('screen').width - 40,
  borderRadius: 20,
  marginBottom: 16,
}
